import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  StreamVideo,
  StreamCall,
  useCallStateHooks,
  ParticipantView,
} from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import { Chat, Channel, MessageList, MessageComposer } from "stream-chat-react";
import "stream-chat-react/dist/css/index.css";
import { ArrowLeft, Broadcast, X, ChatCircleDots, Waveform, MagicWand, Megaphone } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { getStreamClients } from "../lib/streamClient";
import VoiceEffectsPanel from "../components/VoiceEffectsPanel";
import GiftLeaderboardBanner from "../components/GiftLeaderboardBanner";
import GiftBattleOverlay from "../components/GiftBattleOverlay";
import LiveChatMessage from "../components/LiveChatMessage";
import AnnouncerPanel from "../components/AnnouncerPanel";
import LiveGiftSplash from "../components/LiveGiftSplash";
import { useAuth } from "../context/AuthContext";
import {
  installVoiceEnhancer,
  setVoiceEnhancement,
  isVoiceEnhancementEnabled,
} from "../lib/voiceEnhancer";

function LocalPreview() {
  const { useLocalParticipant } = useCallStateHooks();
  const local = useLocalParticipant();
  if (!local) {
    return (
      <div className="w-full h-full bg-black/60 flex items-center justify-center text-cream/50 text-xs tracking-[0.25em] uppercase">
        Waiting for camera…
      </div>
    );
  }
  return <ParticipantView participant={local} ParticipantViewUI={null} />;
}

function HostHud({ isLive, viewers }) {
  return (
    <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between pointer-events-none">
      <div className="flex items-center gap-2 crystal-glass px-3 py-1.5 rounded-full">
        <span className={`w-2 h-2 rounded-full ${isLive ? "bg-red-500 animate-pulse" : "bg-cream/40"}`} />
        <span className="text-cream text-[10px] tracking-[0.3em] uppercase font-bold">
          {isLive ? "Live" : "Offstage"}
        </span>
      </div>
      <div className="crystal-glass px-3 py-1.5 rounded-full text-cream text-[10px] tracking-[0.3em] uppercase font-bold">
        {viewers} watching
      </div>
    </div>
  );
}

export default function GoLive() {
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [videoClient, setVideoClient] = useState(null);
  const [chatClient, setChatClient] = useState(null);
  const [call, setCall] = useState(null);
  const [channel, setChannel] = useState(null);
  const [title, setTitle] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [callId, setCallId] = useState(null);
  const [phase, setPhase] = useState("compose"); // compose | preview | live | ending
  const [viewers, setViewers] = useState(0);
  const [showChat, setShowChat] = useState(true);
  const [voiceOn, setVoiceOn] = useState(true);
  const [showFx, setShowFx] = useState(false);
  const [showAnnouncer, setShowAnnouncer] = useState(false);
  const callRef = useRef(null);

  // Install the WebAudio enhancer once on mount. Free tier: host (and 1 co-host)
  // get real-time noise-suppress + compressor + EQ. Everyone else is fine to
  // toggle it locally at zero server cost.
  useEffect(() => {
    installVoiceEnhancer();
    setVoiceEnhancement(true);
  }, []);

  // Init clients once
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { videoClient: v, chatClient: c } = await getStreamClients();
        if (!mounted) return;
        setVideoClient(v);
        setChatClient(c);
      } catch (e) {
        toast.error("Could not connect to live service");
        nav("/live");
      }
    })();
    return () => { mounted = false; };
  }, [nav]);

  // Cleanup on unmount: leave call if still alive
  useEffect(() => {
    return () => {
      if (callRef.current) {
        callRef.current.leave().catch(() => {});
      }
    };
  }, []);

  const startStream = async () => {
    if (!title.trim()) {
      toast.error("Give your stream a title first");
      return;
    }
    if (!videoClient || !chatClient) return;
    try {
      const { data } = await api.post("/live/start", { title: title.trim() });
      setSessionId(data.session_id);
      setCallId(data.call_id);

      const c = videoClient.call(data.call_type, data.call_id);
      callRef.current = c;
      await c.join({ create: true });
      await c.camera.enable();
      await c.microphone.enable();
      setCall(c);

      const ch = chatClient.channel("livestream", data.call_id, { name: data.title });
      await ch.watch();
      setChannel(ch);
      setPhase("preview");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to start stream");
    }
  };

  const goLive = async () => {
    if (!call) return;
    try {
      await call.goLive({ start_hls: true });
      setPhase("live");
      toast.success("You're live! 🇯🇲");
    } catch (e) {
      toast.error("Could not go live: " + (e.message || ""));
    }
  };

  const endStream = async () => {
    setPhase("ending");
    try {
      if (sessionId) await api.post("/live/end", { session_id: sessionId });
    } catch (e) { /* non-fatal */ }
    try {
      if (call) await call.leave();
      callRef.current = null;
    } catch (e) { /* ignore */ }
    toast.success("Stream ended. Replay will appear soon.");
    nav("/live");
  };

  /**
   * Toggle real-time voice enhancement. Because Stream Video's MicrophoneManager
   * grabs the mic once at enable() time, we must cycle disable → enable to
   * re-acquire the (now enhanced/raw) MediaStream through our patched
   * getUserMedia wrapper. Doing so is instant and doesn't interrupt the call.
   */
  const toggleVoice = async () => {
    const next = !voiceOn;
    setVoiceOn(next);
    setVoiceEnhancement(next);
    try {
      if (call?.microphone && phase !== "compose") {
        await call.microphone.disable();
        await call.microphone.enable();
      }
      toast.success(
        next ? "Voice enhancement ON — clearer sound." : "Voice enhancement OFF.",
      );
    } catch (e) {
      toast.error("Could not re-acquire mic. Try again.");
    }
  };

  // Compose form
  if (phase === "compose") {
    return (
      <div className="min-h-[100dvh] bg-ink pb-32 px-5 pt-8" data-testid="golive-compose">
        <button onClick={() => nav("/live")} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream mb-4" data-testid="golive-back">
          <ArrowLeft size={18} />
        </button>
        <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Go Live</p>
        <h1 className="text-cream text-4xl font-black tracking-tighter mt-1">Light up the diaspora</h1>
        <p className="text-cream/60 mt-2 text-sm">Set a title, fire up the camera, and start broadcasting.</p>

        <div className="mt-8 space-y-4">
          <div>
            <span className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Stream title</span>
            <input
              data-testid="golive-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Soca Sunday Sessions 🎶"
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya transition-colors"
            />
          </div>
          <button
            data-testid="golive-start"
            onClick={startStream}
            disabled={!title.trim() || !videoClient}
            className="w-full py-4 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm hover:bg-papaya-hover transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
          >
            <Broadcast size={18} weight="fill" /> Open Camera
          </button>
        </div>
      </div>
    );
  }

  // Preview or live
  return (
    <StreamVideo client={videoClient}>
      {call && (
        <StreamCall call={call}>
          <div className="h-[100dvh] bg-black relative overflow-hidden flex flex-col" data-testid="golive-room">
            <HostHud isLive={phase === "live"} viewers={viewers} />

            <div className="flex-1 relative">
              <LocalPreview />

              {/* Chat overlay (mobile-first slide) */}
              {chatClient && channel && showChat && (
                <div className="absolute bottom-0 left-0 right-0 max-h-[45vh] bg-gradient-to-t from-ink via-ink/80 to-transparent pointer-events-none">
                  <div className="pointer-events-auto bg-ink/60 backdrop-blur-md border-t border-white/10 h-full">
                    <Chat client={chatClient} theme="str-chat__theme-dark">
                      <Channel channel={channel}>
                        <div className="h-[40vh] flex flex-col">
                          <div className="flex-1 overflow-hidden">
                            <MessageList Message={LiveChatMessage} />
                          </div>
                          <MessageComposer />
                        </div>
                      </Channel>
                    </Chat>
                  </div>
                </div>
              )}
            </div>

            <div className="p-4 flex items-center gap-3 bg-ink border-t border-white/10 z-30">
              <button
                onClick={() => setShowChat((v) => !v)}
                className="w-12 h-12 rounded-full crystal-glass text-cream flex items-center justify-center"
                data-testid="golive-toggle-chat"
                title="Toggle chat"
              >
                <ChatCircleDots size={20} />
              </button>
              <button
                onClick={toggleVoice}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                  voiceOn
                    ? "bg-papaya/25 text-papaya papaya-glow"
                    : "crystal-glass text-cream/60"
                }`}
                data-testid="golive-toggle-voice"
                title={voiceOn ? "Voice enhancement ON" : "Voice enhancement OFF"}
              >
                <Waveform size={20} weight={voiceOn ? "fill" : "regular"} />
              </button>
              <button
                onClick={() => setShowFx(true)}
                className="w-12 h-12 rounded-full crystal-glass text-papaya flex items-center justify-center"
                data-testid="golive-voice-effects"
                title="Voice Effects Library"
              >
                <MagicWand size={20} weight="fill" />
              </button>
              <button
                onClick={() => setShowAnnouncer(true)}
                className="w-12 h-12 rounded-full crystal-glass text-jam-gold flex items-center justify-center"
                data-testid="golive-announcer"
                title="AI Announcer"
              >
                <Megaphone size={20} weight="fill" />
              </button>
              {phase === "preview" ? (
                <button
                  onClick={goLive}
                  className="flex-1 py-3.5 rounded-full bg-papaya text-ink font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2"
                  data-testid="golive-go"
                >
                  <Broadcast size={18} weight="fill" /> Start Broadcast
                </button>
              ) : (
                <button
                  onClick={endStream}
                  className="flex-1 py-3.5 rounded-full bg-red-500 text-cream font-black tracking-[0.2em] uppercase text-sm flex items-center justify-center gap-2"
                  data-testid="golive-end"
                >
                  <X size={18} /> End Stream
                </button>
              )}
            </div>
          </div>
          {phase === "live" && sessionId && (
            <div className="absolute top-[72px] left-4 right-4 z-20 pointer-events-none space-y-2">
              <div className="pointer-events-auto">
                <GiftBattleOverlay sessionId={sessionId} isHost={true} />
              </div>
              <GiftLeaderboardBanner sessionId={sessionId} />
            </div>
          )}
          {phase === "live" && sessionId && <LiveGiftSplash sessionId={sessionId} isAdmin={me?.role === "admin"} />}
          <VoiceEffectsPanel open={showFx} onClose={() => setShowFx(false)} />
          <AnnouncerPanel open={showAnnouncer} onClose={() => setShowAnnouncer(false)} sessionId={sessionId} />
        </StreamCall>
      )}
    </StreamVideo>
  );
}
